import React, {Component} from 'react';
import {connect} from 'react-redux';
import {withTranslation} from 'react-i18next';
import {Redirect} from 'react-router';
import {Button, CircularProgress, MenuItem, TextField} from '@material-ui/core';
import axios from 'axios';
import moment from 'moment';
import {loadPerformanceDashboard} from '../Api/_ApiPerformance';
import {setBreadcrumb} from '../Redux/Action/breadcrumbAction';
import {dashboardDates, eventCounts, isPerformanceAdmin, screenTimings, summaryTimings, todayHkt} from '../utils/performanceDashboard';
import PerformanceChart from '../components/100Include/PerformanceChart';
import PageNotFound from './PageNotFound';
import './PerformanceDashboard.css';

export class PerformanceDashboard extends Component {
    state = {
        start: moment.utc(todayHkt()).subtract(13, 'days').format('YYYY-MM-DD'),
        end: todayHkt(), sources: null, loading: false, error: '', grouping: 'auction',
    };

    componentDidMount() {
        if (isPerformanceAdmin(this.props.auth)) {
            this.props.setBreadcrumb({breadcrumbData: [{
                title: this.props.t('Sidebar:performance'),
                link: `/${this.props.i18n.language}/performance`,
            }]});
            this.load();
        }
    }

    componentDidUpdate(previous) {
        if (isPerformanceAdmin(previous.auth) && !isPerformanceAdmin(this.props.auth)) {
            this.cancel();
            this.setState({sources: null, loading: false});
        }
    }

    componentWillUnmount() {
        this.cancel();
    }

    cancel = () => {
        if (this.request) this.request.cancel();
        clearTimeout(this.timeout);
        this.request = null;
    };

    load = async () => {
        if (!isPerformanceAdmin(this.props.auth)) return;
        const {start, end} = this.state;
        if (!dashboardDates(start, end).length) {
            this.setState({error: 'Choose 1–31 days in HKT, ending no later than today.', sources: null});
            return;
        }
        this.cancel();
        const request = axios.CancelToken.source();
        this.request = request;
        this.timeout = setTimeout(() => request.cancel(), 30000);
        this.setState({loading: true, sources: null, error: ''});
        try {
            const sources = await loadPerformanceDashboard(start, end, request.token);
            if (this.request === request && isPerformanceAdmin(this.props.auth)) {
                this.setState({sources, loading: false});
            }
        } catch (error) {
            if (this.request === request) {
                this.setState({error: error.message, loading: false});
            }
        } finally {
            if (this.request === request) {
                clearTimeout(this.timeout);
                this.request = null;
            }
        }
    };

    changeDate = field => event => {
        this.cancel();
        this.setState({[field]: event.target.value, sources: null, loading: false, error: ''});
    };

    renderCharts() {
        const {sources, start, end, grouping} = this.state;
        if (!sources) return null;
        const available = sources.filter(source => source.status === 'available');
        const missing = sources.filter(source => source.status !== 'available');
        const events = available.reduce((all, source) => all.concat(source.events), []);
        const dates = dashboardDates(start, end);
        const stages = summaryTimings(events);
        const unknownCountries = events.filter(event => !event.device_country_code).length;
        const groups = grouping === 'auction' ? available.map(source => source.id)
            : Array.from(new Set(events.map(event => event.platform || 'Unknown'))).sort();
        const groupNames = grouping === 'auction' ? available.map(source => source.name) : groups;
        return <React.Fragment>
            <p><strong>{events.length.toLocaleString()} recorded data points</strong> · {available.length} reporting auction platforms.
                {' '}All dates use server receipt time in Hong Kong (UTC+8).
                {end === todayHkt() && ' Today is a partial day.'}
            </p>
            {missing.length > 0 && <details className="performance-warning" open>
                <summary>{missing.length} platforms excluded — missing data is not counted as zero.</summary>
                <ul>{missing.map(source => <li key={source.id}>{source.name}: {
                    {unavailable: 'dashboard endpoint not available', forbidden: 'access denied',
                        range: 'date range rejected; try fewer days', failed: 'request failed or timed out'}[source.status]
                }</li>)}</ul>
            </details>}
            {!available.length ? <p role="status">No platform returned performance data. Check endpoint availability and access, then retry.</p>
                : !events.length ? <p role="status">No recorded events in this date range.</p>
                    : <div className="performance-grid">
                        <div>
                            <PerformanceChart id="screen-timing" title="Group screen → first render"
                                data={screenTimings(events, dates)} series={['Median (nearest rank)', 'Slowest']}
                                mode="bars" unit="seconds" emptyText="No successful group-screen render measurements." />
                            <p className="performance-note">Successful auction_groups.list operations; n is the daily sample count.
                                Small samples and different builds do not establish a performance trend.</p>
                        </div>
                        <div>
                            <PerformanceChart id="summary-timing" title="Summary-request timing breakdown"
                                data={stages.data} series={['DB work', 'Other timed server work', 'Client time outside server measurement']}
                                mode="stacked" unit="seconds" emptyText="No complete, consistent summary-request timings." />
                            <p className="performance-note">Slowest {stages.data.length} of {stages.measured} measured requests (maximum 20 shown).
                                {' '}{stages.total - stages.measured} requests have missing or inconsistent timings.
                                Client time outside the server includes more than network latency.</p>
                        </div>
                        <div>
                            <PerformanceChart id="country-counts" title="Data points: HK / non-HK"
                                data={eventCounts(events.filter(event => event.device_country_code), dates, ['HK', 'non-HK'],
                                    event => event.device_country_code === 'HK' ? 'HK' : 'non-HK')}
                                series={['HK', 'non-HK']} unit="count" />
                            <p className="performance-note">Device-country settings, not verified physical location.
                                {' '}{unknownCountries} events with missing country settings are excluded from this graph.</p>
                        </div>
                        <div>
                            <TextField select label="Group by" value={grouping}
                                onChange={event => this.setState({grouping: event.target.value})}>
                                <MenuItem value="auction">Auction platform</MenuItem>
                                <MenuItem value="client">Client platform (iOS / Android)</MenuItem>
                            </TextField>
                            <PerformanceChart id="platform-counts" title="Data points by platform"
                                data={eventCounts(events, dates, groups, event => grouping === 'auction'
                                    ? event.supplier_id : event.platform || 'Unknown')}
                                series={groupNames} unit="count" />
                            <p className="performance-note">Counts include both request and operation events; they are not user or bid counts.</p>
                        </div>
                    </div>}
        </React.Fragment>;
    }

    render() {
        const {auth, i18n, t} = this.props;
        if (!auth.auth) return <Redirect to={`/${i18n.language}/login`} />;
        if (!isPerformanceAdmin(auth)) return <PageNotFound />;
        const {start, end, loading, error} = this.state;
        return <main className="performance-dashboard">
            <h1>{t('Sidebar:performance')}</h1>
            <div className="performance-controls">
                <TextField label="From (HKT)" type="date" value={start} onChange={this.changeDate('start')}
                    InputLabelProps={{shrink: true}} inputProps={{max: todayHkt()}} />
                <TextField label="To (HKT)" type="date" value={end} onChange={this.changeDate('end')}
                    InputLabelProps={{shrink: true}} inputProps={{max: todayHkt()}} />
                <Button variant="contained" color="primary" onClick={this.load} disabled={loading}>Refresh</Button>
            </div>
            {error && <p role="alert" className="performance-warning">{error}</p>}
            {loading && <div role="status"><CircularProgress size={24} /> Loading performance data…</div>}
            {this.renderCharts()}
        </main>;
    }
}

export default withTranslation()(connect(state => ({auth: state.auth}), {setBreadcrumb})(PerformanceDashboard));
